import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import {
  FaArrowLeft, FaClipboardCheck, FaFileImport, FaFileExcel, FaFilePdf, FaFileDownload, FaSave, FaTrash, FaEye, FaSyncAlt,
} from 'react-icons/fa';
import api from '../../services/api';
import {
  ANOMALY_LABELS, SEVERITY, calibrate, controlerJournal, defaultParams, frDate, periodeLabel,
} from './journalPaieControle';
import { downloadTemplate, exportAsTemplate, isExcelFile, readTemplate } from './journalPaieExcel';
import { exportJournalPdf } from './journalPaiePdf';

const fmt = (n, dec = 2) => (n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
const fmtDh = (n) => `${fmt(n)} DH`;

// Paramètres éditables : [clé, libellé, type] — 'pct' affiché en %, stocké en fraction
const PARAM_FIELDS = [
  ['cnss_taux', 'CNSS salariale', 'pct'],
  ['cnss_plafond', 'Plafond CNSS (DH)', 'dh'],
  ['amo_taux', 'AMO salariale', 'pct'],
  ['compl_sal_taux', 'Complémentaire salariale (CIMR, mutuelle…)', 'pct'],
  ['cnss_pat_taux', 'CNSS patronale (plafonnée)', 'pct'],
  ['pat_non_plafonne_taux', 'AF + FP + AMO patronale', 'pct'],
  ['compl_pat_taux', 'Complémentaire patronale', 'pct'],
  ['fraispro_taux', 'Frais pro — taux tranche 1', 'pct'],
  ['fraispro_plafond', 'Frais pro — plafond tranche 1 (DH)', 'dh'],
  ['fraispro_seuil', 'Frais pro — seuil de bascule (DH)', 'dh'],
  ['fraispro_taux2', 'Frais pro — taux tranche 2', 'pct'],
  ['fraispro_plafond2', 'Frais pro — plafond tranche 2 (DH)', 'dh'],
  ['charge_famille_montant', 'Réduction IR / personne à charge (DH)', 'dh'],
  ['charge_famille_max', 'Personnes à charge max', 'dh'],
  ['smig_horaire', 'SMIG horaire (DH)', 'dh'],
  ['tolerance', 'Tolérance des écarts (DH)', 'dh'],
  ['seuil_variation', 'Seuil de variation vs M-1', 'pct'],
];

const SeverityBadge = ({ severity }) => (severity
  ? <Badge bg={SEVERITY[severity].bg} text={severity === 'alerte' ? 'dark' : undefined}>{SEVERITY[severity].label}</Badge>
  : <Badge bg="success">OK</Badge>);

const Ecart = ({ value, tol = 0.05 }) => {
  if (value == null) return <span className="text-muted">—</span>;
  const bad = Math.abs(value) > tol;
  return <span className={bad ? 'text-danger fw-bold' : 'text-success'}>{bad ? fmt(value) : '✓'}</span>;
};

const KpiCard = ({ label, value, sub, variant }) => (
  <Card className="h-100 shadow-sm">
    <Card.Body className="py-2">
      <div className="small text-muted">{label}</div>
      <div className={`fs-5 fw-bold ${variant ? `text-${variant}` : ''}`}>{value}</div>
      {sub && <div className="small text-muted">{sub}</div>}
    </Card.Body>
  </Card>
);

const ControleJournalPaie = ({ onBack }) => {
  const [journaux, setJournaux] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Journal affiché : { ...données, saved, file? }
  const [journal, setJournal] = useState(null);
  const [params, setParams] = useState(null);
  const [filter, setFilter] = useState('anomalies');
  const [codeFilter, setCodeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const fileInputRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get('/journal-paie');
      setJournaux(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Impossible de charger les journaux');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openJournal = (data, extra) => {
    setJournal({ ...data, ...extra });
    setParams(calibrate(data.lignes, defaultParams(data.periode_debut)));
    setExpanded(new Set());
    setFilter('anomalies');
    setCodeFilter('');
    setSearch('');
  };

  // Envoi au backend : PDF en multipart, modèle Excel déjà lu en JSON
  const postSource = (url, source, replace) => {
    if (source.excel) return api.post(url, { journal: source.excel, ...(replace ? { replace: '1' } : {}) });
    const fd = new FormData();
    fd.append('file', source.file);
    if (replace) fd.append('replace', '1');
    return api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setSuccess(''); setBusy(true);
    try {
      const source = isExcelFile(file) ? { excel: await readTemplate(file) } : { file };
      const { data } = await postSource('/journal-paie/parse', source, false);
      openJournal({ ...data, totaux: data.total }, { saved: false, source });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Lecture du fichier impossible');
    } finally {
      setBusy(false);
    }
  };

  const saveJournal = async () => {
    if (!journal?.source) return;
    if (journal.existing_id && !window.confirm(`Un journal existe déjà pour ${periodeLabel(journal.periode_debut)} (${journal.societe || 'société inconnue'}). Le remplacer ?`)) return;
    setBusy(true); setError('');
    try {
      const { data } = await postSource('/journal-paie', journal.source, !!journal.existing_id);
      setJournal((j) => ({ ...j, id: data.id, saved: true, source: null, existing_id: null }));
      setSuccess(data.replaced ? 'Journal remplacé et enregistré.' : 'Journal enregistré.');
      loadList();
    } catch (err) {
      setError(err?.response?.data?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  };

  const viewJournal = async (id) => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const { data } = await api.get(`/journal-paie/${id}`);
      openJournal(data, { saved: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Impossible de charger le journal');
    } finally {
      setBusy(false);
    }
  };

  const deleteJournal = async (j) => {
    if (!window.confirm(`Supprimer le journal de ${periodeLabel(j.periode_debut)} (${j.societe || '—'}) ?`)) return;
    try {
      await api.delete(`/journal-paie/${j.id}`);
      setSuccess('Journal supprimé.');
      if (journal?.id === j.id) setJournal(null);
      loadList();
    } catch (err) {
      setError(err?.response?.data?.message || 'Suppression impossible');
    }
  };

  const result = useMemo(() => (journal && params ? controlerJournal(journal, params) : null), [journal, params]);

  const visibleRows = useMemo(() => {
    if (!result) return [];
    const q = search.trim().toLowerCase();
    return result.rows.filter((r) => {
      if (filter === 'anomalies' && !(r.severity === 'erreur' || r.severity === 'alerte')) return false;
      if (filter === 'erreur' && r.severity !== 'erreur') return false;
      if (filter === 'alerte' && r.severity !== 'alerte') return false;
      if (codeFilter && !r.anomalies.some((a) => a.code === codeFilter)) return false;
      if (q && !`${r.matricule} ${r.nom}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [result, filter, codeFilter, search]);

  const toggleRow = (m) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(m)) n.delete(m); else n.add(m);
    return n;
  });

  const setParam = (key, type, raw) => {
    const v = raw === '' ? 0 : Number(String(raw).replace(',', '.'));
    if (Number.isNaN(v)) return;
    setParams((p) => ({ ...p, [key]: type === 'pct' ? v / 100 : v }));
  };

  const exportExcel = () => {
    if (!result) return;
    const lignes = result.rows.map((r) => ({
      Matricule: r.matricule,
      Nom: r.nom,
      'Date entrée': frDate(r.date_entree),
      'Date sortie': frDate(r.date_sortie),
      Heures: r.heures,
      Brut: r.brut,
      'Brut abattu': r.brut_abattu,
      'Indemnités non imposables': r.indemnitesNonImposables,
      'Ret. Sal.': r.ret_sal,
      'Ret. Sal. attendue': r.retSalAttendu,
      'Ret. Pat.': r.ret_pat,
      'Ret. Pat. attendue': r.retPatAttendu,
      'Frais pro': r.fraisPro,
      'Net imposable': r.net_imposable,
      'Net imposable attendu': r.niAttendu,
      'IR barème': r.irBareme,
      'Divers plus': r.divers_plus,
      'Divers moins': r.divers_moins,
      'Autres retenues (Divers moins − IR)': r.autresRetenues,
      'Net à payer': r.net_a_payer,
      'Net M-1': r.prev ? r.prev.net_a_payer : '',
      'Coût employeur': r.coutEmployeur,
      Statut: r.severity ? SEVERITY[r.severity].label : 'OK',
      Anomalies: r.anomalies.map((a) => `[${SEVERITY[a.severity].label}] ${a.message}`).join(' | '),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lignes), 'Contrôle');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.totaux.map((t) => ({
      Colonne: t.label, 'Somme des lignes': t.somme, 'TOTAL GENERAL journal': t.journal, Écart: t.ecart,
    }))), 'Totaux');
    if (result.sortants.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.sortants.map((s) => ({
        Matricule: s.matricule, Nom: s.nom, 'Date sortie': frDate(s.date_sortie), 'Net M-1': s.net_a_payer, Justifié: s.justifie ? 'Oui' : 'Non',
      }))), 'Absents vs M-1');
    }
    const soc = String(journal.societe || 'societe').replace(/[^a-z0-9]+/gi, '_');
    XLSX.writeFile(wb, `controle_journal_paie_${soc}_${(journal.periode_debut || '').slice(0, 7)}.xlsx`);
  };

  // ── Vue liste ──────────────────────────────────────────────────────────────
  const header = (
    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
      <div>
        <Button
          variant="link"
          className="ps-0 text-decoration-none"
          onClick={journal ? () => { setJournal(null); setSuccess(''); } : onBack}
        >
          <FaArrowLeft className="me-1" />{journal ? 'Retour aux journaux' : 'Retour aux outils'}
        </Button>
        <h4 className="mb-0 mt-1">
          <FaClipboardCheck className="me-2" />
          Contrôle du journal de paie
          {journal && <span className="text-muted fw-normal"> — {periodeLabel(journal.periode_debut)} · {journal.societe || '—'}</span>}
        </h4>
      </div>
      <div className="d-flex gap-2">
        <input ref={fileInputRef} type="file" accept="application/pdf,.pdf,.xlsx,.xls" hidden onChange={handleFile} />
        <Button variant="success" onClick={() => fileInputRef.current.click()} disabled={busy}>
          {busy ? <Spinner size="sm" className="me-1" /> : <FaFileImport className="me-1" />}
          Importer un journal (PDF ou Excel)
        </Button>
        <Button variant="outline-secondary" onClick={downloadTemplate} title="Télécharger le modèle Excel avec des lignes d'exemple">
          <FaFileDownload className="me-1" />Modèle Excel
        </Button>
      </div>
    </div>
  );

  const alerts = (
    <>
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
    </>
  );

  if (!journal) {
    return (
      <div>
        {header}
        {alerts}
        <Card className="shadow-sm">
          <Card.Header className="fw-bold">Journaux importés</Card.Header>
          <Card.Body className="p-0">
            {loadingList ? (
              <div className="p-4 text-center"><Spinner size="sm" /> Chargement…</div>
            ) : journaux.length === 0 ? (
              <div className="p-4 text-muted">
                Aucun journal enregistré. Importez le PDF « Journal de Paie » exporté depuis Cegid PAIE-GRH
                (ou le modèle Excel rempli) :
                chaque bulletin est recalculé (CNSS, AMO, frais pro, net imposable, IR, net à payer), les totaux
                sont rapprochés du TOTAL GENERAL et le journal est comparé au mois précédent enregistré.
              </div>
            ) : (
              <Table hover responsive size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Période</th>
                    <th>Société</th>
                    <th>Établissement</th>
                    <th className="text-end">Salariés</th>
                    <th className="text-end">Brut</th>
                    <th className="text-end">Net à payer</th>
                    <th>Importé</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {journaux.map((j) => (
                    <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => viewJournal(j.id)}>
                      <td className="fw-semibold text-capitalize">{periodeLabel(j.periode_debut)}</td>
                      <td>{j.societe || '—'}</td>
                      <td>{j.etablissement}</td>
                      <td className="text-end">{j.nb_salaries}</td>
                      <td className="text-end">{fmt(j.totaux?.brut)}</td>
                      <td className="text-end">{fmt(j.totaux?.net_a_payer)}</td>
                      <td className="small text-muted">
                        {new Date(j.created_at).toLocaleDateString('fr-FR')}{j.imported_by_name ? ` · ${j.imported_by_name}` : ''}
                      </td>
                      <td className="text-end text-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => viewJournal(j.id)} title="Contrôler">
                          <FaEye />
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => deleteJournal(j)} title="Supprimer">
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ── Vue contrôle ───────────────────────────────────────────────────────────
  const { kpis, bySeverity, byCode, totaux, sortants } = result;
  const totauxOk = totaux.every((t) => t.ecart == null || Math.abs(t.ecart) <= 0.05)
    && (result.nbJournal == null || result.nbJournal === result.nbLignes);
  const varNet = kpis.prevNet ? (kpis.net - kpis.prevNet) / kpis.prevNet : null;

  return (
    <div>
      {header}
      {alerts}

      <Card className="shadow-sm mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2">
          <div className="small">
            Période du <strong>{frDate(journal.periode_debut)}</strong> au <strong>{frDate(journal.periode_fin)}</strong>
            {' · '}Établissement : <strong>{journal.etablissement}</strong>
            {journal.fichier_nom && <> · Fichier : <span className="text-muted">{journal.fichier_nom}</span></>}
            {' · '}Comparé à : <strong>{journal.precedent ? periodeLabel(journal.precedent.periode_debut) : 'aucun journal antérieur enregistré'}</strong>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {journal.saved
              ? <Badge bg="success">Enregistré</Badge>
              : (
                <Button variant="primary" size="sm" onClick={saveJournal} disabled={busy}>
                  <FaSave className="me-1" />{journal.existing_id ? 'Remplacer le journal existant' : 'Enregistrer ce journal'}
                </Button>
              )}
            <Button variant="outline-success" size="sm" onClick={exportExcel} title="Résultat du contrôle : lignes, anomalies, totaux">
              <FaFileExcel className="me-1" />Export contrôle
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => exportJournalPdf(journal)} title="Journal de paie au format de l'édition Cegid">
              <FaFilePdf className="me-1" />Export PDF
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => exportAsTemplate(journal)} title="Données du journal au format du modèle Excel (modifiable puis réimportable)">
              <FaFileDownload className="me-1" />Export format modèle
            </Button>
          </div>
        </Card.Body>
      </Card>

      {!journal.saved && journal.existing_id && (
        <Alert variant="warning" className="py-2">Un journal est déjà enregistré pour cette période et cette société — l'enregistrement le remplacera.</Alert>
      )}
      {journal.warnings?.length > 0 && (
        <Alert variant="warning" className="py-2">
          <div className="fw-bold">Avertissements de lecture du PDF</div>
          <ul className="mb-0">{journal.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </Alert>
      )}

      <Row className="g-2 mb-3">
        <Col xs={6} md={4} lg={2}><KpiCard label="Salariés" value={result.nbLignes} sub={result.nbJournal != null ? `Journal : ${result.nbJournal}` : null} /></Col>
        <Col xs={6} md={4} lg={2}><KpiCard label="Masse brute" value={fmtDh(kpis.brut)} /></Col>
        <Col xs={6} md={4} lg={2}>
          <KpiCard
            label="Net à payer"
            value={fmtDh(kpis.net)}
            sub={varNet != null ? `${varNet >= 0 ? '+' : ''}${fmt(varNet * 100, 1)} % vs M-1` : null}
          />
        </Col>
        <Col xs={6} md={4} lg={2}><KpiCard label="Coût employeur" value={fmtDh(kpis.coutEmployeur)} sub={`Ret. Pat. ${fmt(kpis.retPat, 0)}`} /></Col>
        <Col xs={6} md={4} lg={2}><KpiCard label="Erreurs" value={bySeverity.erreur} variant={bySeverity.erreur ? 'danger' : 'success'} /></Col>
        <Col xs={6} md={4} lg={2}><KpiCard label="Alertes" value={bySeverity.alerte} variant={bySeverity.alerte ? 'warning' : 'success'} sub={`${bySeverity.info} info`} /></Col>
      </Row>

      <Accordion className="mb-3" alwaysOpen defaultActiveKey={totauxOk ? [] : ['totaux']}>
        <Accordion.Item eventKey="totaux">
          <Accordion.Header>
            Rapprochement des totaux&nbsp;
            {totauxOk ? <Badge bg="success">Conforme</Badge> : <Badge bg="danger">Écarts</Badge>}
          </Accordion.Header>
          <Accordion.Body className="p-0">
            <Table size="sm" responsive className="mb-0">
              <thead className="table-light">
                <tr><th>Colonne</th><th className="text-end">Somme des lignes</th><th className="text-end">TOTAL GENERAL</th><th className="text-end">Écart</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Nombre de salariés</td>
                  <td className="text-end">{result.nbLignes}</td>
                  <td className="text-end">{result.nbJournal ?? '—'}</td>
                  <td className="text-end"><Ecart value={result.nbJournal == null ? null : result.nbLignes - result.nbJournal} tol={0} /></td>
                </tr>
                {totaux.map((t) => (
                  <tr key={t.key}>
                    <td>{t.label}</td>
                    <td className="text-end">{fmt(t.somme)}</td>
                    <td className="text-end">{fmt(t.journal)}</td>
                    <td className="text-end"><Ecart value={t.ecart} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="params">
          <Accordion.Header>
            Paramètres de contrôle&nbsp;
            <span className="small text-muted">
              (complémentaire détectée : {fmt(params.compl_sal_taux * 100)} % sal. / {fmt(params.compl_pat_taux * 100)} % pat. · barème IR {params.bareme === 'LF2025' ? 'LF 2025' : 'avant 2025'})
            </span>
          </Accordion.Header>
          <Accordion.Body>
            <Row className="g-2">
              {PARAM_FIELDS.map(([key, label, type]) => (
                <Col key={key} xs={12} sm={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="small mb-0">{label}{type === 'pct' ? ' (%)' : ''}</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="any"
                      value={Number.isFinite(params[key]) ? Number((type === 'pct' ? params[key] * 100 : params[key]).toFixed(4)) : ''}
                      placeholder={Number.isFinite(params[key]) ? '' : 'illimité'}
                      onChange={(e) => setParam(key, type, e.target.value)}
                    />
                  </Form.Group>
                </Col>
              ))}
              <Col xs={12} sm={6} lg={3}>
                <Form.Group>
                  <Form.Label className="small mb-0">Barème IR</Form.Label>
                  <Form.Select size="sm" value={params.bareme} onChange={(e) => setParams((p) => ({ ...p, bareme: e.target.value }))}>
                    <option value="ANCIEN">Avant 2025 (0 % jusqu'à 2 500 DH)</option>
                    <option value="LF2025">LF 2025 (0 % jusqu'à 3 333 DH)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2 mt-3">
              <Button size="sm" variant="outline-secondary" onClick={() => setParams(calibrate(journal.lignes, defaultParams(journal.periode_debut)))}>
                <FaSyncAlt className="me-1" />Réinitialiser (défauts de la période + détection auto)
              </Button>
            </div>
            <div className="small text-muted mt-2">
              L'IR n'a pas de colonne propre dans le journal Cegid : il est inclus dans « Divers moins » avec les autres
              retenues (prêts, avances) et la reprise d'arrondi. Le contrôle signale seulement un « Divers moins »
              inférieur à l'IR minimal possible (barème − réduction maximale pour charges de famille).
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {journal.precedent && (
          <Accordion.Item eventKey="sortants">
            <Accordion.Header>
              Présents en {periodeLabel(journal.precedent.periode_debut)}, absents de ce journal&nbsp;
              <Badge bg={sortants.some((s) => !s.justifie) ? 'warning' : 'secondary'} text={sortants.some((s) => !s.justifie) ? 'dark' : undefined}>
                {sortants.length}
              </Badge>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              {sortants.length === 0 ? <div className="p-3 text-muted">Aucun.</div> : (
                <Table size="sm" responsive className="mb-0">
                  <thead className="table-light"><tr><th>Matricule</th><th>Nom</th><th>Date de sortie</th><th className="text-end">Net M-1</th><th></th></tr></thead>
                  <tbody>
                    {sortants.map((s) => (
                      <tr key={s.matricule}>
                        <td>{s.matricule}</td>
                        <td>{s.nom}</td>
                        <td>{frDate(s.date_sortie) || '—'}</td>
                        <td className="text-end">{fmt(s.net_a_payer)}</td>
                        <td>{s.justifie ? <Badge bg="secondary">Sortie</Badge> : <Badge bg="warning" text="dark">Non justifié</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>

      <Card className="shadow-sm">
        <Card.Header className="d-flex flex-wrap gap-2 align-items-center">
          <span className="fw-bold me-2">Bulletins</span>
          <Form.Select size="sm" style={{ width: 'auto' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="anomalies">Erreurs + alertes ({bySeverity.erreur + bySeverity.alerte})</option>
            <option value="erreur">Erreurs ({bySeverity.erreur})</option>
            <option value="alerte">Alertes ({bySeverity.alerte})</option>
            <option value="all">Tous ({result.nbLignes})</option>
          </Form.Select>
          <Form.Select size="sm" style={{ width: 'auto' }} value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)}>
            <option value="">Tous les contrôles</option>
            {Object.entries(byCode).map(([code, n]) => <option key={code} value={code}>{ANOMALY_LABELS[code] || code} ({n})</option>)}
          </Form.Select>
          <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="Matricule ou nom…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <span className="small text-muted ms-auto">{visibleRows.length} ligne(s) · cliquer une ligne pour le détail</span>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive size="sm" className="mb-0 align-middle small">
            <thead className="table-light">
              <tr>
                <th>Matricule</th>
                <th>Nom</th>
                <th className="text-end">Heures</th>
                <th className="text-end">Brut</th>
                <th className="text-end">Ret. Sal.</th>
                <th className="text-end">Ret. Pat.</th>
                <th className="text-end">Net imposable</th>
                <th className="text-end">IR barème</th>
                <th className="text-end">Divers moins</th>
                <th className="text-end">Net à payer</th>
                {journal.precedent && <th className="text-end">Net M-1</th>}
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr><td colSpan={12} className="text-center text-muted p-3">Aucune ligne pour ce filtre.</td></tr>
              )}
              {visibleRows.map((r, idx) => {
                const has = (code) => r.anomalies.some((a) => a.code === code);
                const open = expanded.has(r.matricule);
                return (
                  <React.Fragment key={`${r.matricule}-${idx}`}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => toggleRow(r.matricule)}>
                      <td>{r.matricule}</td>
                      <td>
                        {r.nom}
                        {r.date_sortie && <div className="text-muted" style={{ fontSize: '0.75em' }}>sortie {frDate(r.date_sortie)}</div>}
                      </td>
                      <td className="text-end">{r.heures == null ? <span className="text-muted">—</span> : fmt(r.heures)}</td>
                      <td className="text-end">{fmt(r.brut)}</td>
                      <td className={`text-end ${has('RET_SAL') ? 'text-danger fw-bold' : ''}`}>
                        {fmt(r.ret_sal)}
                        {r.profilSal === 'base' && r.brut > 0 && params.compl_sal_taux > 0 && <div className="text-muted" style={{ fontSize: '0.75em' }}>sans compl.</div>}
                      </td>
                      <td className={`text-end ${has('RET_PAT') ? 'text-danger fw-bold' : ''}`}>{fmt(r.ret_pat)}</td>
                      <td className={`text-end ${has('NET_IMPOSABLE') ? 'text-danger fw-bold' : ''}`}>{fmt(r.net_imposable)}</td>
                      <td className="text-end text-muted">{fmt(r.irBareme)}</td>
                      <td className={`text-end ${has('IR') || has('DIVERS_MOINS_NEG') ? 'text-warning fw-bold' : ''}`}>{fmt(r.divers_moins)}</td>
                      <td className={`text-end fw-semibold ${has('NET') ? 'text-danger' : ''}`}>{fmt(r.net_a_payer)}</td>
                      {journal.precedent && (
                        <td className={`text-end ${has('VAR_BRUT') || has('VAR_NET') ? 'text-warning fw-bold' : 'text-muted'}`}>
                          {r.prev ? fmt(r.prev.net_a_payer) : 'nouveau'}
                        </td>
                      )}
                      <td><SeverityBadge severity={r.severity} /> {r.anomalies.length > 0 && <span className="text-muted">{r.anomalies.length}</span>}</td>
                    </tr>
                    {open && (
                      <tr className="table-light">
                        <td colSpan={12}>
                          <Row className="g-3 py-1">
                            <Col md={6}>
                              {r.anomalies.length === 0
                                ? <div className="text-success">Aucune anomalie détectée.</div>
                                : (
                                  <ul className="mb-0 ps-3">
                                    {r.anomalies.map((a) => (
                                      <li key={a.code}><SeverityBadge severity={a.severity} /> {a.message}</li>
                                    ))}
                                  </ul>
                                )}
                            </Col>
                            <Col md={6}>
                              <Table size="sm" borderless className="mb-0">
                                <tbody>
                                  <tr><td className="text-muted">Entrée / sortie</td><td className="text-end">{frDate(r.date_entree) || '—'} / {frDate(r.date_sortie) || '—'}</td></tr>
                                  <tr><td className="text-muted">Brut abattu (dont non imposable)</td><td className="text-end">{fmt(r.brut_abattu)} ({fmt(r.indemnitesNonImposables)})</td></tr>
                                  <tr><td className="text-muted">Ret. Sal. attendue / Ret. Pat. attendue</td><td className="text-end">{fmt(r.retSalAttendu)} / {fmt(r.retPatAttendu)}</td></tr>
                                  <tr><td className="text-muted">Frais pro · Net imposable attendu</td><td className="text-end">{fmt(r.fraisPro)} · {fmt(r.niAttendu)}</td></tr>
                                  <tr><td className="text-muted">Divers moins − IR barème (autres retenues)</td><td className="text-end">{fmt(r.autresRetenues)}</td></tr>
                                  <tr><td className="text-muted">Net recalculé</td><td className="text-end">{fmt(r.netCalcule)}</td></tr>
                                  <tr><td className="text-muted">Coût employeur</td><td className="text-end">{fmt(r.coutEmployeur)}</td></tr>
                                </tbody>
                              </Table>
                            </Col>
                          </Row>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ControleJournalPaie;
